import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useCallback, useEffect, useMemo} from 'react';
import isEqual from 'react-fast-compare';
import {useDispatch} from 'react-redux';

import BenchlingConfig from 'BenchlingConfig';
import DataConnector from 'connect/DataConnector';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import {EMPTY_SEARCH_RESULT_PROPS} from 'notebook/mediocre/search/SearchResultProps';
import SelectableNotePart from 'notebook/mediocre/selection/block/SelectableNotePart';
import GroupTitleBar from 'notebook/notes/groups/GroupTitleBar';
import {StructuredTableSpreadsheetProps} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import {
  assertIsStructuredTableNotePart,
  isStructuredTableNotePart,
} from 'notebook/notes/groups/util/assertIsStructuredTableNotePart';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import {NoteEditor, NoteEditorProps} from 'notebook/notes/NoteEditorTypes';
import {
  ActionFunctionContext,
  useActionFunctionContext,
} from 'notebook/structured-tables/action-functions/ActionFunctionContext';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {UnknownStructuredTable} from 'notebook/structured-tables/StructuredTableConstants';
import StructuredTableFormOrView from 'notebook/structured-tables/StructuredTableFormOrView';
import {getSelectorIdsFromFields} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {collapsedTables, selectTableCollapsed} from 'notebook/tables/collapse/reduceCollapsedTablesUI';
import withSpreadsheetEvaluation, {
  EnhancedProps,
  ExternalProps,
} from 'notebook/tables/spreadsheets/withSpreadsheetEvaluation';
import {TableOpDiffMap} from 'notebook/tables/TableConstants';
import {Note, NotePart} from 'notebook/util/NoteUtilConstants';
import {useAppSelector} from 'redux-stores/reduceApp';
import SchemaFieldSelectorStore from 'registry/schema-field-selector/SchemaFieldSelectorStore';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import {GRAY} from 'ui-library/Colors';
import OverflowMenu from 'ui-library/menus/OverflowMenu';
import preserveShallowEquality from 'util/preserveShallowEquality';

import {getNoteLinkedGroupAdapters} from './NoteLinkedGroupAdapters';
import {
  GroupActionFunctionParameters,
  NoteLinkedGroup,
  NoteLinkedGroupAdapter,
  UnknownNoteLinkedGroup,
} from './NoteLinkedGroupConstants';
import groupKindToIcon from './util/groupKindToIcon';
import maybeAsyncUpdateContainedNotePart from './util/maybeAsyncUpdateContainedNotePart';
import {
  getExternalPropsForSpreadsheetEval,
  makeTableItemPropsByName,
  makeTableItemPropsByNameForIsolatedEval,
} from './util/NoteLinkedGroupViewSpreadsheetProps';

/*
This component should show the name and the action functions for the Group
and then render each of the note linked objects contained within the group.
*/

export interface NoteLinkedGroupProps {
  notePartCid: string;
  group: UnknownNoteLinkedGroup;
  editor: NoteEditor;
  editorProps: NoteEditorProps;
  spreadsheetProps: StructuredTableSpreadsheetProps;
}

interface ConnectedProps {
  selectorsById: {[selectorId: string]: SchemaFieldSelector} | null;
}

function GroupActionButtons<TGroupMetadata extends {}, TCreateGroupExtras extends {}>(
  props: GroupActionFunctionParameters<TGroupMetadata, TCreateGroupExtras>
): JSX.Element {
  const actionFunctions = props.noteLinkedGroupAdapter.getActionFunctions();

  // When collapsed, only show the primary action function.
  return (
    <>
      {!props.isCollapsed &&
        actionFunctions.secondary?.map((actionFunction, index) => (
          <React.Fragment key={index}>{actionFunction.renderToolbarButton(props)}</React.Fragment>
        ))}
      {!props.isCollapsed && actionFunctions.overflow?.length ? (
        <OverflowMenu
          options={actionFunctions.overflow
            .map((actionFunction) => actionFunction.getOverflowMenuItem(props))
            .filter((option) => option != null)}
        />
      ) : null}
      {actionFunctions.primary?.renderToolbarButton(props)}
    </>
  );
}

// ---------------------------------------------------------------------------
// Isolated evaluation path (groups that do NOT opt in to entry-level eval)
// ---------------------------------------------------------------------------

interface ActionFunctionContextProps {
  actionFunctionContext: ActionFunctionContext;
}
export type NoteLinkedGroupIsolatedViewProps = NoteLinkedGroupProps &
  ExternalProps<NotePart | UnknownNoteLinkedGroup, Note | UnknownNoteLinkedGroup> &
  EnhancedProps &
  ActionFunctionContextProps & {
    isCollapsed: boolean;
    onToggleCollapsed: () => void;
  };

// withSpreadsheetEvaluation triggers "Type instantiation is excessively deep and possibly infinite"
// when used with functional components, so this must remain a class component.
// eslint-disable-next-line react/prefer-stateless-function
class NoteLinkedGroupIsolatedView<
  TGroupMetadata extends {},
  TCreateGroupExtras extends {},
> extends React.Component<NoteLinkedGroupIsolatedViewProps & ConnectedProps> {
  override render(): JSX.Element {
    const {props} = this;
    const {
      group,
      editor,
      editorProps,
      notePartCid,
      selectorsById,
      actionFunctionContext,
      isCollapsed,
      onToggleCollapsed,
    } = props;
    const adapter = getNoteLinkedGroupAdapters()[group.kind];
    const handleContainedNotePartContentUpdate = async (
      containedNotePartIndex: number,
      content: object
    ): Promise<void> => {
      const oldContainedNotePart = group.containedNoteParts[containedNotePartIndex];
      const updatedContainedNotePart = editor.getItemWithUpdatedContent(oldContainedNotePart, content);
      await maybeAsyncUpdateContainedNotePart(group, editor, notePartCid, updatedContainedNotePart);
    };
    const {evaluatedTableMap, tableItemPropsByName} = makeTableItemPropsByNameForIsolatedEval(props);
    const actionFunctionParameters: GroupActionFunctionParameters<TGroupMetadata, TCreateGroupExtras> = {
      entryOrTemplateId: editorProps.fileId,
      noteLinkedGroup: group as NoteLinkedGroup<TGroupMetadata>,
      noteLinkedGroupAdapter: adapter as NoteLinkedGroupAdapter<TGroupMetadata, TCreateGroupExtras>,
      locked: editorProps.locked,
      isCollapsed,
      evaluatedTableMap,
      actionFunctionContext,
      getItemWithUpdatedContent: editor.getItemWithUpdatedContent,
      getIdFromCidFn: editorProps.getIdFromCid,
      updateGroup: (noteLinkedGroup: NoteLinkedGroup<{}>) => {
        editor.itemUpdateHandler(editor.getIndex(notePartCid), {item: noteLinkedGroup});
      },
      insertAfterGroup: (noteParts: Array<NotePart>, cidIdMap: CidIdMap) => {
        editor.props.registerObjects(cidIdMap);
        editor.itemInsertHandler(editor.getIndex(notePartCid), ...noteParts);
      },
      selectorsById,
      groupResyncRequired: false,
    };
    // Print preview should display all groups expanded.
    const isExpanded = !isCollapsed || props.editorProps.viewer;
    const printableContainerClass = props.editorProps.viewer ? 'printable-container-scaleForPrint' : '';
    return (
      <SelectableNotePart indentation={0} className={printableContainerClass}>
        <div
          className={classNames(
            css(styles.groupWrapper),
            BenchlingConfig.TEMP_UPDATED_MIXTURE_PREP_EXPORT_STYLE && 'notebook-export--group-wrapper'
          )}
        >
          <GroupTitleBar
            groupTypeLabel={adapter.getDisplayName(group)}
            groupName={group.name}
            icon={groupKindToIcon(group.kind)}
            rightHeaderControls={<GroupActionButtons {...actionFunctionParameters} />}
            isCollapsed={isCollapsed}
            onToggleCollapsed={onToggleCollapsed}
            viewer={props.editorProps.viewer}
          />
          {isExpanded && (
            <div
              className={classNames(
                css(styles.partsWrapper),
                BenchlingConfig.TEMP_UPDATED_MIXTURE_PREP_EXPORT_STYLE && 'notebook-export--parts-wrapper'
              )}
            >
              {group.containedNoteParts.map((containedNotePart, index) => {
                // For now, NoteLinkedGroup only supports structured tables, which may be relaxed in the future.
                assertIsStructuredTableNotePart(containedNotePart);
                const tableAdapter = getStructuredTableAdapters()[containedNotePart.item.kind];
                return (
                  <StructuredTableFormOrView
                    key={containedNotePart.cid}
                    editor={editor}
                    // HACK: withSpreadsheetEvaluation HOF injects a handful of props that override the spreadsheet
                    // behavior given by editorProps.
                    editorProps={{...editorProps, ...props}}
                    index={index}
                    notePart={containedNotePart}
                    onUpdateContent={handleContainedNotePartContentUpdate}
                    isInGroup={true}
                    itemProps={tableItemPropsByName[containedNotePart.item.name]}
                    adapter={tableAdapter}
                    overrideDefaultTableProps={{
                      disableMaximizedTables: true,
                      isCollapsible: false,
                    }}
                    // TODO(BNCH-40532): Support NLG in Ctrl-F search
                    {...EMPTY_SEARCH_RESULT_PROPS}
                  />
                );
              })}
            </div>
          )}
        </div>
      </SelectableNotePart>
    );
  }
}

const NoteLinkedGroupIsolatedViewWithSpreadsheet = withSpreadsheetEvaluation<
  NoteLinkedGroupProps &
    ExternalProps<NotePart | UnknownNoteLinkedGroup, Note | UnknownNoteLinkedGroup> &
    ActionFunctionContextProps & {
      isCollapsed: boolean;
      onToggleCollapsed: () => void;
    },
  NotePart | UnknownNoteLinkedGroup,
  Note | UnknownNoteLinkedGroup
>(NoteLinkedGroupIsolatedView);

// ---------------------------------------------------------------------------
// Entry-level evaluation path (groups that opt in to entry-level eval)
// This component duplicates some structure from NoteLinkedGroupIsolatedView above.
// The duplication is temporary: once all NLG types migrate to entry-level eval
// and the feature gate is removed, NoteLinkedGroupIsolatedView and its
// withSpreadsheetEvaluation wrapper will be deleted.
// ---------------------------------------------------------------------------

export type NoteLinkedGroupEntryLevelViewProps = NoteLinkedGroupProps &
  ConnectedProps &
  ActionFunctionContextProps & {
    isCollapsed: boolean;
    onToggleCollapsed: () => void;
  };

function NoteLinkedGroupEntryLevelView(props: NoteLinkedGroupEntryLevelViewProps): JSX.Element {
  const {
    group,
    editor,
    editorProps,
    notePartCid,
    selectorsById,
    spreadsheetProps,
    actionFunctionContext,
    isCollapsed,
    onToggleCollapsed,
  } = props;
  const adapter = getNoteLinkedGroupAdapters()[group.kind];

  const handleContainedNotePartContentUpdate = useCallback(
    async (
      containedNotePartIndex: number,
      content: object,
      _selectors?: never,
      tableOp?: TableOpDiffMap
    ): Promise<void> => {
      const oldContainedNotePart = group.containedNoteParts[containedNotePartIndex];
      const updatedContainedNotePart = editor.getItemWithUpdatedContent(oldContainedNotePart, content);
      await maybeAsyncUpdateContainedNotePart(group, editor, notePartCid, updatedContainedNotePart, tableOp);
    },
    [group, editor, notePartCid]
  );

  const {cellColors, editingTable, computedTables} = editor.props;
  const {evaluatedTableMap, tableItemPropsByName} = useMemo(
    () => makeTableItemPropsByName(group, editor, spreadsheetProps, cellColors, editingTable, computedTables),
    [group, editor, spreadsheetProps, cellColors, editingTable, computedTables]
  );

  const groupResyncRequired = adapter.isGroupResyncRequired(group, evaluatedTableMap);

  const actionFunctionParameters: GroupActionFunctionParameters<{}, {}> = {
    entryOrTemplateId: editorProps.fileId,
    noteLinkedGroup: group as NoteLinkedGroup<{}>,
    noteLinkedGroupAdapter: adapter as NoteLinkedGroupAdapter<{}, {}>,
    locked: editorProps.locked,
    isCollapsed,
    evaluatedTableMap,
    actionFunctionContext,
    getItemWithUpdatedContent: editor.getItemWithUpdatedContent,
    getIdFromCidFn: editorProps.getIdFromCid,
    updateGroup: (noteLinkedGroup: NoteLinkedGroup<{}>) => {
      editor.itemUpdateHandler(editor.getIndex(notePartCid), {item: noteLinkedGroup});
    },
    insertAfterGroup: (noteParts: Array<NotePart>, cidIdMap: CidIdMap) => {
      editor.props.registerObjects(cidIdMap);
      editor.itemInsertHandler(editor.getIndex(notePartCid), ...noteParts);
    },
    selectorsById,
    groupResyncRequired,
  };
  // Print preview should display all groups expanded.
  const isExpanded = !isCollapsed || editorProps.viewer;
  const printableContainerClass = editorProps.viewer ? 'printable-container-scaleForPrint' : '';
  return (
    <SelectableNotePart indentation={0} className={printableContainerClass}>
      <div
        className={classNames(
          css(styles.groupWrapper),
          BenchlingConfig.TEMP_UPDATED_MIXTURE_PREP_EXPORT_STYLE && 'notebook-export--group-wrapper'
        )}
      >
        <GroupTitleBar
          groupTypeLabel={adapter.getDisplayName(group)}
          groupName={group.name}
          icon={groupKindToIcon(group.kind)}
          rightHeaderControls={<GroupActionButtons {...actionFunctionParameters} />}
          isCollapsed={isCollapsed}
          onToggleCollapsed={onToggleCollapsed}
          viewer={editorProps.viewer}
        />
        {isExpanded && (
          <div
            role="presentation"
            className={classNames(
              css(styles.partsWrapper),
              BenchlingConfig.TEMP_UPDATED_MIXTURE_PREP_EXPORT_STYLE && 'notebook-export--parts-wrapper'
            )}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            onMouseOver={(e) => {
              e.stopPropagation();
            }}
          >
            {group.containedNoteParts.map((containedNotePart, index) => {
              assertIsStructuredTableNotePart(containedNotePart);
              const tableAdapter = getStructuredTableAdapters()[containedNotePart.item.kind];
              return (
                <StructuredTableFormOrView
                  key={containedNotePart.cid}
                  editor={editor}
                  editorProps={editorProps}
                  index={index}
                  notePart={containedNotePart}
                  onUpdateContent={handleContainedNotePartContentUpdate}
                  isInGroup={true}
                  itemProps={tableItemPropsByName[containedNotePart.item.name]}
                  adapter={tableAdapter}
                  overrideDefaultTableProps={{
                    disableMaximizedTables: true,
                    isCollapsible: false,
                  }}
                  // TODO(BNCH-40532): Support NLG in Ctrl-F search
                  {...EMPTY_SEARCH_RESULT_PROPS}
                />
              );
            })}
          </div>
        )}
      </div>
    </SelectableNotePart>
  );
}

// ---------------------------------------------------------------------------
// Routing component
// ---------------------------------------------------------------------------

function UnconnectedNoteLinkedGroupView(props: NoteLinkedGroupProps & ConnectedProps): JSX.Element {
  const adapter = getNoteLinkedGroupAdapters()[props.group.kind];
  const actionFunctionContext = useActionFunctionContext();

  const isCollapsed =
    useAppSelector((state) => selectTableCollapsed(state, props.group.provenanceUuid)) ?? false;
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(collapsedTables.actions.init({id: props.group.provenanceUuid, isCollapsed: false}));
  }, [dispatch, props.group.provenanceUuid]);

  const onToggleCollapsed = useCallback(() => {
    dispatch(collapsedTables.actions.toggle({id: props.group.provenanceUuid}));
  }, [dispatch, props.group.provenanceUuid]);

  if (adapter.supportsEntryLevelEvaluation()) {
    return (
      <NoteLinkedGroupEntryLevelView
        {...props}
        actionFunctionContext={actionFunctionContext}
        isCollapsed={isCollapsed}
        onToggleCollapsed={onToggleCollapsed}
      />
    );
  }

  const externalProps = getExternalPropsForSpreadsheetEval(
    props.editorProps,
    props.group,
    props.editor,
    props.notePartCid
  );
  return (
    <NoteLinkedGroupIsolatedViewWithSpreadsheet
      {...props}
      {...externalProps}
      actionFunctionContext={actionFunctionContext}
      isCollapsed={isCollapsed}
      onToggleCollapsed={onToggleCollapsed}
    />
  );
}

class NoteLinkedGroupViewConnector extends DataConnector<NoteLinkedGroupProps, ConnectedProps> {
  override stores = [SchemaFieldSelectorStore];

  enhanceProps: () => ConnectedProps;

  constructor(props: NoteLinkedGroupProps) {
    super(props);
    this.enhanceProps = preserveShallowEquality(this.uncachedEnhanceProps);
  }

  getAllSchemaFields(group: UnknownNoteLinkedGroup): Array<SchemaField> {
    return group.containedNoteParts.flatMap((notePart) => {
      if (!isStructuredTableNotePart(notePart)) {
        return [];
      }
      const structuredTable = notePart.item as UnknownStructuredTable;
      return structuredTable.schemas.flatMap((schema) => schema.allFields);
    });
  }

  uncachedEnhanceProps = (): ConnectedProps => {
    const fields = this.getAllSchemaFields(this.props.group);
    const selectorIds = getSelectorIdsFromFields(fields);
    if (selectorIds.length === 0) {
      return {
        selectorsById: {},
      };
    }
    const selectorsById = this.getOrFetchMultipleOptionalFull(
      ManagedModelType.SCHEMA_FIELD_SELECTOR_BY_ID,
      selectorIds
    );
    return {selectorsById};
  };
}

// Only trigger a re-render due to a redux state change if the ConnectedProps change.
export const NoteLinkedGroupView = NoteLinkedGroupViewConnector.connect(UnconnectedNoteLinkedGroupView, {
  shouldPropagateNextState: (a, b) => !isEqual(a, b),
});

export default NoteLinkedGroupView;

const styles = StyleSheet.create({
  groupWrapper: {
    backgroundColor: 'white',
    border: `1px solid ${GRAY}`,
    borderRadius: '4px',
    display: 'inline-block',
    maxWidth: '100%',
    position: 'relative',
    width: '100%',
  },
  partsWrapper: {
    // The contained noteParts need to be stretched outward by a pixel. This ensures that any
    // table borders coincide with the group border, rather than creating a two-pixel border.
    margin: '0 -1px',
  },
});
